import { apiService } from './apiService';
import { API_BASE_URL } from '../config/env';
import {
  ContextAuthor,
  ContextRevisionsPage,
  ContextSaveResult,
  PatientAgentContext,
} from '../models/patientAgentContext';

const url = (patientId: number) => `${API_BASE_URL}/clinic/patient/${patientId}/agent-context/`;

function toAuthor(raw: any): ContextAuthor | null {
  return raw && raw.id != null
    ? { id: Number(raw.id), name: String(raw.name ?? ''), role: String(raw.role ?? '') }
    : null;
}

export function toPatientAgentContext(raw: any, patientId: number): PatientAgentContext {
  return {
    patientId: Number(raw?.patientId ?? patientId),
    text: String(raw?.text ?? ''),
    version: Number(raw?.version) || 0,
    updatedAt: raw?.updatedAt ?? null,
    updatedBy: toAuthor(raw?.updatedBy),
    maxLength: Number(raw?.maxLength) || 4000,
  };
}

// A 409 version_conflict carries the version someone else saved in between.
function conflictFrom(err: any, patientId: number): PatientAgentContext | null {
  if (err?.status !== 409 || !err?.response?.current) return null;
  return toPatientAgentContext(err.response.current, patientId);
}

export const patientAgentContextService = {
  get: async (patientId: number): Promise<PatientAgentContext> =>
    toPatientAgentContext(await apiService.get(url(patientId)), patientId),

  // `version` is the one the editor loaded. Saving "" removes the context.
  save: async (patientId: number, text: string, version: number): Promise<ContextSaveResult> => {
    try {
      const data = await apiService.put(url(patientId), { text, version });
      return { ok: true, context: toPatientAgentContext(data, patientId) };
    } catch (err) {
      const conflict = conflictFrom(err, patientId);
      if (conflict) return { ok: false, conflict };
      throw err;
    }
  },

  revisions: async (patientId: number, page = 1, perpage = 20): Promise<ContextRevisionsPage> => {
    const data = await apiService.get(`${url(patientId)}revisions/?page=${page}&perpage=${perpage}`);
    const results: any[] = Array.isArray(data?.results) ? data.results : [];
    return {
      revisions: results.map((r) => ({
        version: Number(r.version),
        text: String(r.text ?? ''),
        author: toAuthor(r.author),
        source: r.source === 'agent_proposal' ? 'agent_proposal' : 'clinician',
        createdAt: String(r.createdAt ?? ''),
      })),
      page: Number(data?.actualPage) || page,
      totalPages: Number(data?.totalPages) || 1,
    };
  },
};
